// PeakEdu Tier 0 WASM — Sentry frontend init skeleton (B14 SD44 PM)
//
// Lazy CDN load để bundle production stay small khi DSN unset.
// Khi VITE_SENTRY_DSN set → tải Sentry SDK từ CDN + init.
// Skip silent nếu DSN empty hoặc network fail (fail-open).
//
// Activate Tuần 7 SD45:
//   1. Boss tạo Sentry org "peakedu" + project "wasm-client" (Browser JavaScript)
//   2. Copy DSN
//   3. Set VITE_SENTRY_DSN trong CF Pages env vars
//   4. Re-deploy WASM Tier 0
//
// Reference: docs/legal/data-processing-record.md § 4 (Sentry processor) — SCC US transfer.
//
// IMPORTANT — Privacy compliance:
//   - PII scrub via beforeSend hook (email, JWT, audio bytes never leave browser)
//   - Trace sample rate 0.1 (10%) để tiết kiệm 5k events/month free quota
//   - environment + release tag để phân biệt production/staging

const CDN_VERSION = '8.43.0';  // Pin version để build reproducible
const CDN_URL = `https://browser.sentry-cdn.com/${CDN_VERSION}/bundle.tracing.min.js`;
const CDN_INTEGRITY = ''; // SD45 boss verify SRI hash khi pin version production

const SENSITIVE_KEYS = new Set([
  'access_token',
  'refresh_token',
  'ws_ticket',
  'authorization',
  'jwt',
  'password',
  'secret',
  'audio',
  'audio_bytes',
  'wav_bytes',
  'pcm_data',
]);

function _scrubObject(obj, depth = 0) {
  if (depth > 4 || obj === null || obj === undefined) return obj;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map((v) => _scrubObject(v, depth + 1));
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (SENSITIVE_KEYS.has(k.toLowerCase())) {
      out[k] = '[REDACTED]';
    } else {
      out[k] = _scrubObject(v, depth + 1);
    }
  }
  return out;
}

function _beforeSend(event) {
  // Scrub request data + breadcrumbs
  try {
    if (event.request?.data) {
      event.request.data = _scrubObject(event.request.data);
    }
    if (event.request?.headers) {
      const h = event.request.headers;
      for (const k of Object.keys(h)) {
        if (SENSITIVE_KEYS.has(k.toLowerCase())) h[k] = '[REDACTED]';
      }
    }
    if (Array.isArray(event.breadcrumbs)) {
      event.breadcrumbs = event.breadcrumbs.map((b) => ({
        ...b,
        data: _scrubObject(b.data),
      }));
    }
  } catch {
    // Scrub fail không nên block send — Sentry vẫn send unscrubbed (best-effort)
  }
  return event;
}

function _loadCdn() {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${CDN_URL}"]`);
    if (existing) {
      // Đã load
      if (window.Sentry) return resolve(window.Sentry);
      existing.addEventListener('load', () => resolve(window.Sentry));
      existing.addEventListener('error', () => reject(new Error('cdn_load_fail')));
      return;
    }
    const script = document.createElement('script');
    script.src = CDN_URL;
    script.async = true;
    script.crossOrigin = 'anonymous';
    if (CDN_INTEGRITY) script.integrity = CDN_INTEGRITY;
    script.addEventListener('load', () => {
      if (window.Sentry) resolve(window.Sentry);
      else reject(new Error('sentry_global_missing'));
    });
    script.addEventListener('error', () => reject(new Error('cdn_load_error')));
    document.head.appendChild(script);
  });
}

/**
 * Init Sentry frontend nếu DSN set. Skip silent nếu DSN empty hoặc CDN load fail.
 *
 * @param {object} opts
 * @param {string} opts.dsn         — VITE_SENTRY_DSN
 * @param {string} opts.environment — VITE_SENTRY_ENVIRONMENT (default "production")
 * @param {number} opts.tracesSampleRate — VITE_SENTRY_TRACES_SAMPLE_RATE (default 0.1)
 * @param {string} [opts.release]   — VITE_SENTRY_RELEASE git SHA hoặc semver
 * @returns {Promise<{enabled: boolean, version?: string, error?: string}>}
 */
export async function initSentry({ dsn, environment, tracesSampleRate, release } = {}) {
  if (!dsn) {
    return { enabled: false, error: 'no_dsn' };
  }
  try {
    const Sentry = await _loadCdn();
    if (!Sentry?.init) {
      return { enabled: false, error: 'sentry_init_missing' };
    }
    Sentry.init({
      dsn,
      environment: environment || 'production',
      release: release || undefined,
      tracesSampleRate: typeof tracesSampleRate === 'number' ? tracesSampleRate : 0.1,
      // Replay/Session tracking OFF default — privacy + quota saving
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      // PII scrub
      sendDefaultPii: false,
      beforeSend: _beforeSend,
      // Ignore noise: Network errors khi user mất internet, ResizeObserver console warn
      ignoreErrors: [
        'NetworkError',
        'TypeError: Failed to fetch',
        'ResizeObserver loop limit exceeded',
        'Non-Error promise rejection captured',
      ],
    });
    return { enabled: true, version: CDN_VERSION };
  } catch (err) {
    // Fail-open — Sentry không block app
    return { enabled: false, error: String(err?.message || err) };
  }
}

/**
 * Manually capture exception (helper khi muốn report explicit error ngoài auto-capture).
 */
export function captureException(err, extra) {
  try {
    if (window.Sentry?.captureException) {
      window.Sentry.captureException(err, { extra: _scrubObject(extra) });
    }
  } catch {
    /* ignore */
  }
}

export default { init: initSentry, capture: captureException };
